import { Directive, OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import {
  StripeTransactionStatus,
  TransactionAdminFilterKey,
  TransactionSummaryDto,
} from '@workspace/data-types';
import {
  ActionButton,
  ActionMenu,
  BaseDataTableComponent,
  DataHeaderFilter,
  DataListColumn,
} from '@workspace/ng-aura';
import { transactionStatusDisplayTransform } from '@workspace/ng-data-access';
import { Observable } from 'rxjs';

export type TransactionColumnKey = 'user' | 'subscription' | 'amount' | 'status' | 'date' | 'createdAt';

@Directive()
export abstract class AbstractTransactionsDataTableCoreComponent<T extends TransactionSummaryDto>
  extends BaseDataTableComponent<T, TransactionColumnKey>
  implements OnInit
{
  override topMenu = signal<ActionMenu | null>(null);
  override columns = signal<DataListColumn<TransactionColumnKey>[]>([
    { key: 'user', label: 'Utilisateur', display: true },
    { key: 'subscription', label: 'Abonnement', display: true },
    { key: 'amount', label: 'Montant', display: true },
    { key: 'status', label: 'Statut', display: true },
    { key: 'date', label: 'Date', display: true },
    { key: 'createdAt', label: 'Date de création', display: false },
  ]);
  override topButtons = signal<ActionButton[]>([]);
  override itemActions = signal<ActionButton[]>([
    {
      label: 'Ouvrir',
      icon: 'open_in_new',
      onClick: (tx: TransactionSummaryDto) => this.navigateToTransaction(tx),
      disabled: false,
      color: 'primary',
    },
  ]);

  override filters = signal<DataHeaderFilter<TransactionAdminFilterKey>[]>([
    {
      label: 'Statut',
      column: TransactionAdminFilterKey.Status,
    },
  ]);

  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const status = params.getAll(TransactionAdminFilterKey.Status);
      this.filterChanged({
        changed: true,
        filter: {
          status: status.length ? status : [],
        },
      });
    });
  }

  navigateToUser(tx: TransactionSummaryDto): void {
    if (tx?.user?._id) {
      void this.router.navigate(['/users', tx.user._id]);
    }
  }

  navigateToSubscription(tx: TransactionSummaryDto): void {
    if (tx?.subscription?._id) {
      void this.router.navigate(['/subscriptions', tx.subscription._id]);
    }
  }

  navigateToTransaction(tx: TransactionSummaryDto): void {
    if (tx?._id) {
      void this.router.navigate(['/transactions', tx._id]);
    }
  }

  abstract override getAll(): Observable<T[]>;
  abstract override countAll(): Observable<number>;
  override getDisplayForEntry(key: TransactionAdminFilterKey, rawValue: string): string {
    switch (key) {
      case TransactionAdminFilterKey.Status:
        return transactionStatusDisplayTransform(rawValue as StripeTransactionStatus);
      default:
        return key;
    }
  }
}
